import React, { useState } from "react";
import { Search, Loader2, ChevronDown, ChevronUp, PackageOpen, FileText, MessageSquare, CheckCircle2, Scissors, Sliders, CheckSquare } from "lucide-react";
import { db } from "../firebase/config";
import { doc, updateDoc, collection, query, where, getDocs } from "firebase/firestore";
import { usePOs } from "../hooks/usePOs";
import { useAuth } from "../context/AuthContext";
import { useLogistics } from "../hooks/useLogistics";
import { useVendors } from "../hooks/useVendors";
import { useCompanyLogos } from "../hooks/useCompanyLogos";
import { useInternalPaymentMethods } from "../hooks/usePaymentMethods";
import ChangeRequestModal from "../components/modals/ChangeRequestModal";
import ProcurementNotesModal from "../components/modals/ProcurementNotesModal";
import ProcurementVendorModal from "../components/modals/ProcurementVendorModal";
import OneOffPurchaseModal from "../components/modals/OneOffPurchaseModal";
import SplitLineModal from "../components/modals/SplitLineModal";
import AllPurchasedModal from "../components/modals/AllPurchasedModal";
import POTasksModal from "../components/modals/POTasksModal";

export default function Procurement() {
  const { pos, loading, addPO, updatePO } = usePOs();
  const { currentUser } = useAuth();
  const { shipments, addShipment, updateShipment } = useLogistics();
  const { vendors } = useVendors();
  const { companyLogos } = useCompanyLogos();
  const { internalPaymentMethods: paymentMethods } = useInternalPaymentMethods();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedChangeRequestPo, setSelectedChangeRequestPo] = useState(null);
  const [selectedTasksPo, setSelectedTasksPo] = useState(null);
  const [allPurchasedModal, setAllPurchasedModal] = useState({ open: false, poNumber: "" });

  const isPoFullyDelivered = (poToCheck) => {
    if (!poToCheck || !poToCheck.items || poToCheck.items.length === 0) return false;
    const linkedShipment = shipments.find(s => s.poId === poToCheck.id);
    if (!linkedShipment) return false;
    return poToCheck.items.every(item => {
      const itemStatus = linkedShipment.itemStatuses?.[item.id]?.status || linkedShipment.status || "To Order";
      return itemStatus === "Delivered";
    });
  };
  const [companyFilter, setCompanyFilter] = useState("All");
  
  // One-off purchase state
  const [oneOffModalOpen, setOneOffModalOpen] = useState(false);
  const [submittingOneOff, setSubmittingOneOff] = useState(false);
  const [expandedPOs, setExpandedPOs] = useState({});

  // Notes modal state
  const [notesModal, setNotesModal] = useState({ open: false, poId: null, itemIdx: null, item: null });
  const [tempNotes, setTempNotes] = useState("");

  // Vendor modal state
  const [vendorModal, setVendorModal] = useState({ open: false, poId: null, itemIdx: null, vendorName: "", changing: false });
  const [vendorSearch, setVendorSearch] = useState("");

  // Split Line modal state
  const [splitModal, setSplitModal] = useState({ open: false, po: null, itemIdx: null, originalQty: 0, originalVendorIdx: null });

  // Loading state for All Purchased
  const [purchasingPoId, setPurchasingPoId] = useState(null);

  // Local draft values for inline editable quantity and unit cost
  const [editValues, setEditValues] = useState({});

  const toggleAccordion = (id) => {
    setExpandedPOs(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleOpenSplitModal = (po, itemIdx, originalVendorIdx = null, originalQty) => {
    setSplitModal({
      open: true,
      po,
      itemIdx,
      originalQty,
      originalVendorIdx
    });
  };

  const handleConfirmSplit = async (currentQty, newQty) => {
    const { po, itemIdx, originalVendorIdx } = splitModal;
    if (!po) return;

    try {
      const updatedItems = [...po.items];
      const originalItem = updatedItems[itemIdx];

      let duplicatedItem;
      let updatedOriginalItem;

      if (originalVendorIdx !== null && originalItem.vendors && originalItem.vendors.length > 0) {
        const originalVendors = [...originalItem.vendors];
        const targetVendor = originalVendors[originalVendorIdx];

        // 1. Update original vendor split quantity
        const updatedVendors = originalVendors.map((v, idx) => {
          if (idx !== originalVendorIdx) return v;
          return {
            ...v,
            quantity: currentQty
          };
        });

        // Sum up total quantity for updated original item
        const updatedOriginalQty = updatedVendors.reduce((sum, v) => sum + Number(v.quantity || 0), 0);

        updatedOriginalItem = {
          ...originalItem,
          quantity: updatedOriginalQty,
          vendors: updatedVendors
        };

        // 2. Create duplicated item with ONLY the split vendor split
        const duplicatedVendors = [
          {
            ...targetVendor,
            id: `v-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
            quantity: newQty
          }
        ];

        duplicatedItem = {
          ...originalItem,
          id: `li-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
          quantity: newQty,
          vendors: duplicatedVendors
        };
      } else {
        // Splitting a standard item with no vendors array
        updatedOriginalItem = {
          ...originalItem,
          quantity: currentQty
        };

        duplicatedItem = {
          ...originalItem,
          id: `li-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
          quantity: newQty
        };
      }

      // Update item in updatedItems array
      updatedItems[itemIdx] = updatedOriginalItem;

      // Insert duplicated item directly below the original
      updatedItems.splice(itemIdx + 1, 0, duplicatedItem);

      // Update the PO document in Firestore
      await updatePO(po.id, { items: updatedItems });

      // Sync split to Logistics shipment
      const logisticsCol = collection(db, "logistics");
      const q = query(logisticsCol, where("poId", "==", po.id));
      const qSnapshot = await getDocs(q);
      if (!qSnapshot.empty) {
        const shipmentDoc = qSnapshot.docs[0];
        const shipmentData = shipmentDoc.data();
        const updatedItemStatuses = { ...(shipmentData.itemStatuses || {}) };
        
        const originalStatusEntry = updatedItemStatuses[originalItem.id] || { status: "To Order", orderedDate: "" };
        
        if (duplicatedItem.vendors && duplicatedItem.vendors.length > 0) {
          duplicatedItem.vendors.forEach(v => {
            const origKey = `${originalItem.id}_${v.id}`;
            const dupKey = `${duplicatedItem.id}_${v.id}`;
            updatedItemStatuses[dupKey] = {
              ...(updatedItemStatuses[origKey] || { status: "To Order", orderedDate: "" })
            };
          });
        } else {
          updatedItemStatuses[duplicatedItem.id] = {
            ...originalStatusEntry
          };
        }
        
        await updateDoc(doc(db, "logistics", shipmentDoc.id), {
          itemStatuses: updatedItemStatuses
        });
      }

      // Close the modal
      setSplitModal({ open: false, po: null, itemIdx: null, originalQty: 0, originalVendorIdx: null });
    } catch (err) {
      console.error("Failed to split line item:", err);
    }
  };

  const getPoLogoUrl = (po) => {
    if (po.logoUrl) return po.logoUrl;
    const activeLogo = companyLogos.find(l => l.isActive);
    return activeLogo?.url || "";
  };

  const getCompanyNameForPo = (po) => {
    if (po?.companyName && typeof po.companyName === "string" && po.companyName.trim()) {
      return po.companyName.trim();
    }
    if (po?.logoUrl) {
      const logo = companyLogos.find(l => l.url === po.logoUrl);
      if (logo?.name) return logo.name;
    }
    const activeLogo = companyLogos.find(l => l.isActive);
    return activeLogo?.name || "No Company";
  };

  const availableCompanyNames = React.useMemo(() => {
    const set = new Set();
    (companyLogos || []).forEach(l => { if (l.name) set.add(l.name.trim()); });
    (pos || []).forEach(p => {
      const cName = getCompanyNameForPo(p);
      if (cName && cName !== "No Company" && cName !== "Unknown Company") {
        set.add(cName);
      }
    });
    return Array.from(set).sort();
  }, [companyLogos, pos]);

  const wonPOs = pos.filter(po => {
    if (po.status !== "Won") return false;
    // Show PO on Purchase if there is at least one item that is not ordered/purchased yet
    return (po.items || []).some(item => !item.ordered);
  });

  const filteredPOs = wonPOs.filter(po => {
    const matchesSearch = (po.poNumber && po.poNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
                          (po.clientName && po.clientName.toLowerCase().includes(searchTerm.toLowerCase()));
    
    if (!matchesSearch) return false;

    const matchesCompanyAssignment = (() => {
      if (!companyLogos || companyLogos.length === 0) return true;
      const poLogoUrl = po.logoUrl || companyLogos.find(l => l.isActive)?.url || "";
      if (companyLogos.some(logo => logo.url === poLogoUrl)) return true;
      if (po.companyName && companyLogos.some(logo => logo.name?.toLowerCase() === po.companyName.toLowerCase())) return true;
      return false;
    })();
    if (!matchesCompanyAssignment) return false;

    if (companyFilter !== "All") {
      const companyName = getCompanyNameForPo(po);
      if (companyName.toLowerCase() !== companyFilter.toLowerCase()) return false;
    }

    return true;
  });

  const getPOTotalCost = (items) => {
    if (!items || !Array.isArray(items)) return 0;
    return items.reduce((acc, item) => {
      if (item.vendors && item.vendors.length > 0) {
        const vSum = item.vendors.reduce((sum, v) => {
          const q = parseFloat(v.quantity) || 0;
          const vc = parseFloat(v.vendorPrice ?? v.vendorCost ?? 0) || 0;
          return sum + (q * vc);
        }, 0);
        return acc + vSum + (parseFloat(item.shippingCost) || 0);
      }
      const q = parseFloat(item.quantity) || 0;
      const vCost = parseFloat(item.vendorPrice ?? item.vendorCost ?? 0) || 0;
      const sCost = parseFloat(item.shippingCost) || 0;
      return acc + (q * vCost) + sCost;
    }, 0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Delivered": return "var(--success)";
      case "Transport -> Client": return "var(--primary)";
      case "Warehouse": return "var(--accent)";
      case "Transport -> NC":
      case "Transport -> HQ": return "var(--primary)";
      case "Purchased": return "var(--text-secondary)";
      case "To Order": return "var(--text-muted)";
      default: return "var(--text-muted)";
    }
  };

  const handlePayMethodChange = async (po, row, payMethodValue) => {
    const { originalItemIdx, originalVendorIdx, isSplit } = row;
    const updatedItems = po.items.map((item, idx) => {
      if (idx !== originalItemIdx) return item;
      if (isSplit && originalVendorIdx !== null) {
        const updatedVendors = item.vendors.map((v, vIdx) => {
          if (vIdx !== originalVendorIdx) return v;
          return { ...v, paymentMethod: payMethodValue };
        });
        return { ...item, vendors: updatedVendors };
      } else {
        return { ...item, paymentMethod: payMethodValue };
      }
    });
    try {
      await updatePO(po.id, { items: updatedItems });
    } catch (err) {
      console.error("Failed to update payment method:", err);
    }
  };

  const handleToggleOrdered = async (po, row) => {
    if (!row.paymentMethod?.trim()) {
      alert("Please select a Payment Method before marking this item as Ordered.");
      return;
    }
    const { originalItemIdx, originalVendorIdx, isSplit, vendorKey } = row;
    
    const updatedItems = po.items.map((item, idx) => {
      if (idx !== originalItemIdx) return item;
      
      if (isSplit && originalVendorIdx !== null) {
        const updatedVendors = item.vendors.map((v, vIdx) => {
          if (vIdx !== originalVendorIdx) return v;
          return { ...v, ordered: !v.ordered };
        });
        
        const allVendorsOrdered = updatedVendors.every(v => v.ordered);
        return { 
          ...item, 
          vendors: updatedVendors,
          ordered: allVendorsOrdered
        };
      } else {
        return { ...item, ordered: !item.ordered };
      }
    });

    const newOrderedVal = isSplit 
      ? !po.items[originalItemIdx].vendors[originalVendorIdx].ordered
      : !po.items[originalItemIdx].ordered;

    try {
      // 1. Update PO in Firestore
      await updatePO(po.id, { items: updatedItems });

      // 2. Update linked shipment in logistics
      const linkedShipment = shipments.find(s => s.poId === po.id);
      if (linkedShipment) {
        const today = new Date().toISOString().split("T")[0];

        const statusUpdates = {
          [`itemStatuses.${vendorKey}.status`]: newOrderedVal ? "Purchased" : "To Order",
          [`itemStatuses.${vendorKey}.orderedDate`]: newOrderedVal ? today : ""
        };

        let allOrdered = true;
        updatedItems.forEach(item => {
          if (item.vendors && item.vendors.length > 0) {
            if (!item.vendors.every(v => v.ordered)) allOrdered = false;
          } else {
            if (!item.ordered) allOrdered = false;
          }
        });

        statusUpdates.status = allOrdered ? "Purchased" : "To Order";
        if (allOrdered) {
          statusUpdates.orderedDate = today;
        }

        await updateShipment(linkedShipment.id, statusUpdates);

        if (allOrdered && newOrderedVal) {
          setAllPurchasedModal({
            open: true,
            poNumber: po.poNumber || "PO"
          });
        }
      }
    } catch (err) {
      console.error("Failed to toggle ordered:", err);
    }
  };

  const handleEstArrivalRangeChange = async (po, row, field, dateValue) => {
    const { originalItemIdx, originalVendorIdx, isSplit } = row;
    const updatedItems = po.items.map((item, idx) => {
      if (idx !== originalItemIdx) return item;
      if (isSplit && originalVendorIdx !== null) {
        const updatedVendors = item.vendors.map((v, vIdx) => {
          if (vIdx !== originalVendorIdx) return v;
          const currentStart = field === "start" ? dateValue : (v.estArrivalStart || (v.estArrival && !v.estArrival.includes(" to ") ? v.estArrival : (v.estArrival?.split(" to ")[0] || "")));
          const currentEnd = field === "end" ? dateValue : (v.estArrivalEnd || (v.estArrival?.includes(" to ") ? v.estArrival.split(" to ")[1] : ""));
          const formattedCombined = currentStart && currentEnd 
            ? `${currentStart} to ${currentEnd}` 
            : (currentStart || currentEnd || "");
          return { 
            ...v, 
            estArrivalStart: currentStart,
            estArrivalEnd: currentEnd,
            estArrival: formattedCombined
          };
        });
        return { ...item, vendors: updatedVendors };
      } else {
        const currentStart = field === "start" ? dateValue : (item.estArrivalStart || (item.estArrival && !item.estArrival.includes(" to ") ? item.estArrival : (item.estArrival?.split(" to ")[0] || "")));
        const currentEnd = field === "end" ? dateValue : (item.estArrivalEnd || (item.estArrival?.includes(" to ") ? item.estArrival.split(" to ")[1] : ""));
        const formattedCombined = currentStart && currentEnd 
          ? `${currentStart} to ${currentEnd}` 
          : (currentStart || currentEnd || "");
        return { 
          ...item, 
          estArrivalStart: currentStart,
          estArrivalEnd: currentEnd,
          estArrival: formattedCombined
        };
      }
    });
    try { 
      await updatePO(po.id, { items: updatedItems }); 
    } catch (err) { 
      console.error("Failed to update est arrival range:", err); 
    }
  };

  const handleEstArrivalChange = (po, row, dateValue) => {
    return handleEstArrivalRangeChange(po, row, "start", dateValue);
  };

  const handleQuantityChange = async (po, row, newQty) => {
    const { originalItemIdx, originalVendorIdx, isSplit } = row;
    const parsedQty = newQty === "" ? 0 : parseFloat(newQty);
    const updatedItems = po.items.map((item, idx) => {
      if (idx !== originalItemIdx) return item;
      if (isSplit && originalVendorIdx !== null) {
        const updatedVendors = item.vendors.map((v, vIdx) => {
          if (vIdx !== originalVendorIdx) return v;
          return { ...v, quantity: parsedQty };
        });
        const totalSplitQty = updatedVendors.reduce((sum, v) => sum + (parseFloat(v.quantity) || 0), 0);
        return { ...item, quantity: totalSplitQty, vendors: updatedVendors };
      } else {
        return { ...item, quantity: parsedQty };
      }
    });
    try {
      await updatePO(po.id, { items: updatedItems });
    } catch (err) {
      console.error("Failed to update quantity:", err);
    }
  };

  const handleUnitCostChange = async (po, row, newCost) => {
    const { originalItemIdx, originalVendorIdx, isSplit } = row;
    const parsedCost = newCost === "" ? 0 : parseFloat(newCost);
    const updatedItems = po.items.map((item, idx) => {
      if (idx !== originalItemIdx) return item;
      if (isSplit && originalVendorIdx !== null) {
        const updatedVendors = item.vendors.map((v, vIdx) => {
          if (vIdx !== originalVendorIdx) return v;
          const existingQuoted = (v.quotedUnitCost !== undefined && v.quotedUnitCost !== null)
            ? v.quotedUnitCost
            : (v.vendorPrice ?? item.vendorPrice ?? 0);
          return {
            ...v,
            quotedUnitCost: existingQuoted,
            vendorPrice: parsedCost,
            vendorCost: parsedCost
          };
        });
        return { ...item, vendors: updatedVendors };
      } else {
        const existingQuoted = (item.quotedUnitCost !== undefined && item.quotedUnitCost !== null)
          ? item.quotedUnitCost
          : (item.vendorPrice ?? 0);
        return {
          ...item,
          quotedUnitCost: existingQuoted,
          vendorPrice: parsedCost,
          vendorCost: parsedCost
        };
      }
    });
    try {
      await updatePO(po.id, { items: updatedItems });
    } catch (err) {
      console.error("Failed to update unit cost:", err);
    }
  };

  const handleQtyBlur = async (po, row) => {
    const val = editValues[`${row.vendorKey}_qty`];
    if (val !== undefined) {
      await handleQuantityChange(po, row, val);
      setEditValues(prev => {
        const copy = { ...prev };
        delete copy[`${row.vendorKey}_qty`];
        return copy;
      });
    }
  };

  const handleCostBlur = async (po, row) => {
    const val = editValues[`${row.vendorKey}_cost`];
    if (val !== undefined) {
      await handleUnitCostChange(po, row, val);
      setEditValues(prev => {
        const copy = { ...prev };
        delete copy[`${row.vendorKey}_cost`];
        return copy;
      });
    }
  };

  const openNotesModal = (poId, row) => {
    setNotesModal({ open: true, poId, row });
    setTempNotes("");
  };

  const handleSaveLog = async () => {
    if (!tempNotes.trim()) return;
    const po = pos.find(p => p.id === notesModal.poId);
    if (!po) return;

    const author = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Unknown';
    const newLog = { timestamp: new Date().toISOString(), text: tempNotes.trim(), type: "Manual", author };

    const { originalItemIdx, originalVendorIdx, isSplit } = notesModal.row;

    const updatedItems = po.items.map((item, idx) => {
      if (idx !== originalItemIdx) return item;
      
      if (isSplit && originalVendorIdx !== null) {
        const updatedVendors = item.vendors.map((v, vIdx) => {
          if (vIdx !== originalVendorIdx) return v;
          return { ...v, logs: [...(v.logs || []), newLog] };
        });
        return { ...item, vendors: updatedVendors };
      } else {
        return { ...item, logs: [...(item.logs || []), newLog] };
      }
    });

    try {
      await updatePO(po.id, { items: updatedItems });
      const updatedRow = {
        ...notesModal.row,
        logs: isSplit 
          ? [...(updatedItems[originalItemIdx].vendors[originalVendorIdx].logs || [])]
          : [...(updatedItems[originalItemIdx].logs || [])]
      };
      setNotesModal(prev => ({ ...prev, row: updatedRow }));
      setTempNotes("");
    } catch (err) { console.error("Failed to save log:", err); }
  };

  const handleAllPurchased = async (po) => {
    const missingPayMethod = po.items?.some(item => {
      if (item.vendors && item.vendors.length > 0) {
        return item.vendors.some(v => !v.paymentMethod?.trim());
      }
      return !item.paymentMethod?.trim();
    });
    if (missingPayMethod) {
      alert("Please select a Payment Method for all line items before marking All Purchased.");
      return;
    }
    setPurchasingPoId(po.id);
    try {
      const today = new Date().toISOString().split("T")[0];
      const updatedItems = po.items.map(item => {
        if (item.vendors && item.vendors.length > 0) {
          const updatedVendors = item.vendors.map(v => ({ ...v, ordered: true }));
          return { ...item, vendors: updatedVendors, ordered: true };
        }
        return { ...item, ordered: true };
      });
      await updatePO(po.id, { items: updatedItems });

      const linkedShipment = shipments.find(s => s.poId === po.id);
      if (linkedShipment) {
        const statusUpdates = { status: "Purchased", orderedDate: today };
        
        updatedItems.forEach(item => {
          if (item.vendors && item.vendors.length > 0) {
            item.vendors.forEach(v => {
              const vendorKey = `${item.id}_${v.id}`;
              const currentStatus = linkedShipment.itemStatuses?.[vendorKey]?.status || linkedShipment.status || "To Order";
              if (currentStatus === "To Order") {
                statusUpdates[`itemStatuses.${vendorKey}.status`] = "Purchased";
                statusUpdates[`itemStatuses.${vendorKey}.orderedDate`] = today;
              }
            });
          } else {
            const currentStatus = linkedShipment.itemStatuses?.[item.id]?.status || linkedShipment.status || "To Order";
            if (currentStatus === "To Order") {
              statusUpdates[`itemStatuses.${item.id}.status`] = "Purchased";
              statusUpdates[`itemStatuses.${item.id}.orderedDate`] = today;
            }
          }
        });
        await updateShipment(linkedShipment.id, statusUpdates);
      }

      // Update lastPurchasedDate for each unique vendor in po.items
      const uniqueVendorNames = Array.from(new Set(po.items.flatMap(item => {
        if (item.vendors && item.vendors.length > 0) {
          return item.vendors.map(v => v.vendor);
        }
        return [item.vendor];
      }).filter(Boolean)));
      
      for (const vName of uniqueVendorNames) {
        const vendorObj = getVendorByName(vName);
        if (vendorObj) {
          await updateDoc(doc(db, "vendors", vendorObj.id), {
            lastPurchasedDate: today
          });
        }
      }

      // Trigger All Purchased modal
      setAllPurchasedModal({
        open: true,
        poNumber: po.poNumber || "PO"
      });
    } catch (err) { console.error("Failed to mark all purchased:", err); }
    finally { setPurchasingPoId(null); }
  };

  const openVendorModal = (poId, itemIdx, vendorName) => {
    setVendorModal({ open: true, poId, itemIdx, vendorName, changing: false });
    setVendorSearch("");
  };

  const handleChangeVendor = async (newVendorName) => {
    const po = pos.find(p => p.id === vendorModal.poId);
    if (!po) return;
    const updatedItems = po.items.map((item, idx) =>
      idx === vendorModal.itemIdx ? { ...item, vendor: newVendorName } : item
    );
    try {
      await updatePO(po.id, { items: updatedItems });
      setVendorModal(prev => ({ ...prev, vendorName: newVendorName, changing: false }));
    } catch (err) { console.error("Failed to change vendor:", err); }
  };

  const getVendorByName = (name) => {
    if (!name) return null;
    return vendors.find(v => v.name.toLowerCase() === name.toLowerCase());
  };

  const filteredVendors = vendors.filter(v =>
    v.name.toLowerCase().includes(vendorSearch.toLowerCase())
  );

  const handleOneOffSubmit = async (data) => {
    try {
      setSubmittingOneOff(true);
      
      const trackNum = `TRK-${Math.floor(100000 + Math.random() * 900000)}`;
      const savedAddressObj = localStorage.getItem("facility_address");
      let destination = "";
      if (savedAddressObj) {
        try {
          const addr = JSON.parse(savedAddressObj);
          destination = [addr.address1, addr.address2, addr.city, addr.state, addr.zip, addr.country].filter(Boolean).join(", ");
        } catch (e) {
          console.error("Failed to parse facility address:", e);
        }
      }

      // 1. Add PO document
      const poData = {
        poNumber: data.poNumber,
        clientName: "Atlas Command",
        status: "Won",
        awardedDate: new Date().toISOString().split("T")[0],
        date: new Date().toISOString().split("T")[0],
        items: [
          {
            id: `li-${Date.now()}`,
            description: data.description,
            quantity: data.quantity,
            vendorCost: data.vendorCost,
            vendor: data.vendor,
            shippingCost: data.shippingCost,
            unitPrice: 0,
            ordered: false,
            estArrival: data.estArrival || "",
            logs: data.notes ? [{
              timestamp: new Date().toISOString(),
              text: data.notes,
              type: "Manual",
              author: currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Unknown'
            }] : []
          }
        ],
        shipToAddress: "",
        shipToCity: "",
        shipToState: "",
        shipToZip: ""
      };

      const newPoId = await addPO(poData);

      // 2. Add logistics shipment doc
      const logisticsItem = {
        trackingNumber: trackNum,
        carrier: "Pending Carrier",
        origin: "",
        destination: destination,
        estimatedDelivery: data.estArrival || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        status: "To Order",
        poId: newPoId,
        poNumber: data.poNumber,
        clientName: "Atlas Command",
        awardedDate: new Date().toISOString().split("T")[0]
      };

      await addShipment(logisticsItem);
      setOneOffModalOpen(false);
    } catch (err) {
      console.error("Error creating one-off purchase:", err);
      alert("Failed to create one-off purchase.");
    } finally {
      setSubmittingOneOff(false);
    }
  };

  const fmtCurrency = (val) => `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <div className="page-header-info">
          <h1>Purchase Operations</h1>
          <p>Review items that need to be purchased for awarded/won Purchase Orders.</p>
        </div>
      </div>

      <div className="proc-search-bar" style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
        <div className="proc-search-box" style={{ flex: 1 }}>
          <Search size={18} className="proc-search-icon" />
          <input
            type="text"
            className="form-input proc-search-input"
            placeholder="Search POs by PO # or Client Name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ margin: 0 }}
          />
        </div>
        <select
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)}
          className="form-input"
          style={{ width: "220px", height: "42px", margin: 0, padding: "0.25rem 0.5rem", backgroundColor: "var(--bg-card)" }}
        >
          <option value="All">All Companies</option>
          {availableCompanyNames.map(name => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setOneOffModalOpen(true)}
          style={{ whiteSpace: "nowrap", height: "42px" }}
        >
          One-Off Purchase
        </button>
      </div>

      <div className="proc-accordion-list">
        {loading && pos.length === 0 ? (
          <div className="proc-loading">
            <Loader2 className="proc-spinner" size={36} />
            <span>Loading Awarded Purchase Orders...</span>
          </div>
        ) : filteredPOs.length === 0 ? (
          <div className="proc-empty">
            <PackageOpen size={48} className="proc-empty-icon" />
            <h3>No Awarded POs Found</h3>
            <p>There are no "Won" Purchase Orders currently available for purchase review.</p>
          </div>
        ) : (
          filteredPOs.map(po => {
            const isExpanded = expandedPOs[po.id];
            const totalCost = getPOTotalCost(po.items);
            const linkedShipment = shipments.find(s => s.poId === po.id);
            const alreadyPurchased = (po.items || []).every(item => item.ordered);

            return (
              <div key={po.id} className="proc-card">
                <div className={`proc-card-header ${isExpanded ? "proc-card-header-expanded" : ""}`} onClick={() => toggleAccordion(po.id)}>
                  <div className="proc-card-header-left">
                    <div className="proc-card-icon">
                      {getPoLogoUrl(po) ? (
                        <img 
                          src={getPoLogoUrl(po)} 
                          alt="Company logo" 
                          style={{ width: "24px", height: "24px", borderRadius: "4px", objectFit: "cover" }} 
                        />
                      ) : (
                        <FileText size={24} />
                      )}
                    </div>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                        <h3 className="proc-card-po-number" style={{ margin: 0 }}>{po.poNumber || "Draft PO"}{po.title ? ` — ${po.title}` : ""}</h3>
                        {po.isPriority && (
                          <span className="priority-pill">PRIORITY</span>
                        )}
                      </div>
                      <p className="proc-card-client">
                        Client: <strong>{po.clientName || "Unknown"}</strong>
                        {po.customerPoNumber && (
                          <span style={{ fontSize: "0.85rem", color: "var(--text-secondary)", marginLeft: "0.5rem" }}>
                            • Cust PO: <strong style={{ color: "var(--text-primary)" }}>{po.customerPoNumber}</strong>
                          </span>
                        )}
                        {po.awardedDate && <span className="proc-card-awarded">Awarded: {po.awardedDate}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="proc-card-header-right">
                    <div className="proc-card-cost">
                      <p className="proc-card-cost-label">Total Est. Cost</p>
                      <p className="proc-card-cost-value">{fmtCurrency(totalCost)}</p>
                    </div>
                    {isExpanded ? <ChevronUp size={20} color="var(--text-secondary)" /> : <ChevronDown size={20} color="var(--text-secondary)" />}
                  </div>
                </div>

                {isExpanded && (
                  <div className="proc-card-body">
                    <div className="proc-body-header" style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                      <h4 style={{ margin: 0, marginRight: "auto" }}>Line Items Required for Purchase</h4>
                      <button
                        type="button"
                        className="btn btn-warning"
                        disabled={isPoFullyDelivered(po)}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedChangeRequestPo(po);
                        }}
                        style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          gap: "0.4rem", 
                          padding: "0.4rem 0.8rem",
                          fontSize: "0.85rem",
                          minHeight: "auto",
                          color: "#000000",
                          opacity: isPoFullyDelivered(po) ? 0.4 : 1,
                          cursor: isPoFullyDelivered(po) ? "not-allowed" : "pointer"
                        }}
                        title={isPoFullyDelivered(po) ? "All line items have been delivered. No changes can be made." : "Open Change Request modal"}
                      >
                        <Sliders size={14} color="#000000" />
                        Change Request
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTasksPo(po);
                        }}
                        style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          gap: "0.4rem", 
                          padding: "0.4rem 0.8rem",
                          fontSize: "0.85rem",
                          minHeight: "auto"
                        }}
                        title="View & manage tasks for this PO"
                      >
                        <CheckSquare size={14} />
                        Tasks
                      </button>
                      <button
                        className="btn btn-primary proc-all-purchased-btn"
                        onClick={(e) => { e.stopPropagation(); handleAllPurchased(po); }}
                        disabled={purchasingPoId === po.id || alreadyPurchased}
                        style={{ opacity: alreadyPurchased ? 0.5 : 1, margin: 0 }}
                      >
                        {purchasingPoId === po.id ? <Loader2 size={16} className="proc-spinner" style={{ marginBottom: 0 }} /> : <CheckCircle2 size={16} />}
                        {alreadyPurchased ? "Already Purchased" : "All Purchased"}
                      </button>
                    </div>

                    {(!po.items || po.items.length === 0) ? (
                      <p className="proc-no-items">No line items recorded for this PO.</p>
                    ) : (
                      <div className="proc-table-wrap">
                        <table className="proc-table">
                          <thead>
                            <tr>
                              <th className="proc-th-center" style={{ width: "60px" }}>Ordered</th>
                              <th>Item Description</th>
                              <th style={{ width: "130px" }} className="proc-th-center">Pay Mtd</th>
                              <th style={{ width: "130px" }} className="proc-th-center">Logistics Status</th>
                              <th style={{ width: "140px" }}>Vendor</th>
                              <th className="proc-th-right" style={{ width: "95px" }}>Qty</th>
                              <th className="proc-th-right" style={{ width: "120px" }}>Quoted Unit Cost</th>
                              <th className="proc-th-right" style={{ width: "110px" }}>Unit Cost</th>
                              <th className="proc-th-right" style={{ width: "120px" }}>Total Cost</th>
                              <th className="proc-th-center" style={{ width: "250px" }}>Est Arrival (Range)</th>
                              <th className="proc-th-center" style={{ width: "60px" }}>Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const rows = [];
                              po.items.forEach((item, itemIdx) => {
                                if (item.vendors && item.vendors.length > 0) {
                                  item.vendors.forEach((v, vIdx) => {
                                    rows.push({
                                      ...item,
                                      vendorKey: `${item.id}_${v.id}`,
                                      vendorName: v.vendor,
                                      vendorQty: v.quantity,
                                      vendorCost: v.vendorPrice,
                                      vendorQuotedCost: v.quotedUnitCost !== undefined && v.quotedUnitCost !== null ? v.quotedUnitCost : (v.vendorPrice ?? item.vendorPrice ?? 0),
                                      vendorUrl: v.url,
                                      vendorOrdered: !!v.ordered,
                                      vendorId: v.id,
                                      vendorEstArrival: v.estArrival || item.estArrival || "",
                                      vendorEstArrivalStart: v.estArrivalStart || (v.estArrival && !v.estArrival.includes(" to ") ? v.estArrival : (v.estArrival?.split(" to ")[0] || item.estArrivalStart || item.estArrival || "")),
                                      vendorEstArrivalEnd: v.estArrivalEnd || (v.estArrival?.includes(" to ") ? v.estArrival.split(" to ")[1] : (item.estArrivalEnd || "")),
                                      paymentMethod: v.paymentMethod || item.paymentMethod || "",
                                      originalItemIdx: itemIdx,
                                      originalVendorIdx: vIdx,
                                      isSplit: true,
                                      logs: v.logs || item.logs || []
                                    });
                                  });
                                } else {
                                  rows.push({
                                    ...item,
                                    vendorKey: item.id,
                                    vendorName: item.vendor || "—",
                                    vendorQty: item.quantity || 0,
                                    vendorCost: item.vendorPrice || 0,
                                    vendorQuotedCost: item.quotedUnitCost !== undefined && item.quotedUnitCost !== null ? item.quotedUnitCost : (item.vendorPrice ?? 0),
                                    vendorUrl: item.url || "",
                                    vendorOrdered: !!item.ordered,
                                    vendorId: "default",
                                    vendorEstArrival: item.estArrival || "",
                                    vendorEstArrivalStart: item.estArrivalStart || (item.estArrival && !item.estArrival.includes(" to ") ? item.estArrival : (item.estArrival?.split(" to ")[0] || "")),
                                    vendorEstArrivalEnd: item.estArrivalEnd || (item.estArrival?.includes(" to ") ? item.estArrival.split(" to ")[1] : ""),
                                    paymentMethod: item.paymentMethod || "",
                                    originalItemIdx: itemIdx,
                                    originalVendorIdx: null,
                                    isSplit: false,
                                    logs: item.logs || []
                                  });
                                }
                              });
                              
                              return rows.map((row, idx) => {
                                const q = parseFloat(row.vendorQty) || 0;
                                const vc = parseFloat(row.vendorCost) || 0;
                                const sc = parseFloat(row.isSplit ? 0 : row.shippingCost) || 0;
                                const lineTotal = (q * vc) + sc;
                                const logCount = row.logs ? row.logs.length : 0;
                                const itemStatus = linkedShipment?.itemStatuses?.[row.vendorKey]?.status || linkedShipment?.status || "To Order";
                                const hasPayMethod = !!row.paymentMethod?.trim();
                                const isCheckboxDisabled = (itemStatus !== "To Order" && itemStatus !== "Purchased") || !hasPayMethod;
                                
                                return (
                                  <tr key={row.vendorKey} className={row.vendorOrdered ? "proc-row-ordered" : ""}>
                                    <td className="proc-td-center">
                                      <input 
                                        type="checkbox" 
                                        checked={!!row.vendorOrdered} 
                                        disabled={isCheckboxDisabled}
                                        onChange={() => handleToggleOrdered(po, row)} 
                                        className="proc-checkbox" 
                                        title={!hasPayMethod ? "Select a Payment Method to enable" : isCheckboxDisabled ? "Status does not allow toggling" : "Toggle ordered"}
                                        style={{ 
                                          accentColor: row.vendorOrdered ? "var(--success)" : "var(--primary)",
                                          cursor: isCheckboxDisabled ? "not-allowed" : "pointer",
                                          opacity: isCheckboxDisabled ? 0.4 : 1
                                        }}
                                      />
                                    </td>
                                    <td>
                                      <strong>{row.lineNumber || "01"}</strong> - {row.description || row.name || "Unnamed Item"}
                                    </td>
                                    <td className="proc-td-center">
                                      <select
                                        value={row.paymentMethod || ""}
                                        onChange={(e) => handlePayMethodChange(po, row, e.target.value)}
                                        style={{
                                          padding: "0.25rem 0.4rem",
                                          fontSize: "0.8rem",
                                          borderRadius: "4px",
                                          border: "1px solid var(--border-color)",
                                          backgroundColor: "var(--bg-input)",
                                          color: row.paymentMethod ? "var(--text-primary)" : "var(--text-muted)",
                                          width: "100%",
                                          maxWidth: "125px",
                                          cursor: "pointer"
                                        }}
                                      >
                                        <option value="" style={{ color: "var(--text-muted)" }}>Select...</option>
                                        {paymentMethods.map(pm => (
                                          <option key={pm.id} value={pm.name} style={{ color: "var(--text-primary)" }}>
                                            {pm.name}
                                          </option>
                                        ))}
                                      </select>
                                    </td>
                                    <td className="proc-td-center">
                                      <span 
                                        className="log-item-status-badge" 
                                        style={{ 
                                          color: getStatusColor(itemStatus), 
                                          fontWeight: "600",
                                          fontSize: "0.8rem",
                                          display: "inline-block", 
                                          padding: "0.2rem 0.4rem",
                                          border: `1px solid ${getStatusColor(itemStatus)}`,
                                          borderRadius: "4px",
                                          backgroundColor: "rgba(255,255,255,0.02)"
                                        }}
                                      >
                                        {itemStatus}
                                      </span>
                                    </td>
                                    <td>
                                      <button onClick={() => openVendorModal(po.id, row.originalItemIdx, row.vendorName || "")} className={`proc-vendor-link ${row.vendorName ? "has-vendor" : ""}`}>
                                        {row.vendorName || "—"}
                                      </button>
                                    </td>
                                    <td className="proc-td-right" style={{ verticalAlign: "middle" }}>
                                      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "0.35rem" }}>
                                        <input
                                          type="number"
                                          min="0"
                                          step="any"
                                          className="proc-num-input"
                                          style={{ width: "55px" }}
                                          value={editValues[`${row.vendorKey}_qty`] !== undefined ? editValues[`${row.vendorKey}_qty`] : (row.vendorQty !== undefined ? row.vendorQty : 1)}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            setEditValues(prev => ({ ...prev, [`${row.vendorKey}_qty`]: val }));
                                          }}
                                          onBlur={() => handleQtyBlur(po, row)}
                                          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                                        />
                                        {q > 1 && (
                                          <button
                                            type="button"
                                            onClick={() => handleOpenSplitModal(po, row.originalItemIdx, row.originalVendorIdx, q)}
                                            title="Split this line item into multiple shipments"
                                            style={{
                                              background: "none",
                                              border: "none",
                                              color: "var(--primary)",
                                              cursor: "pointer",
                                              display: "inline-flex",
                                              alignItems: "center",
                                              padding: "2px",
                                              margin: 0,
                                              borderRadius: "3px",
                                              backgroundColor: "rgba(52, 152, 219, 0.1)"
                                            }}
                                          >
                                            <Scissors size={11} />
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                    <td className="proc-td-right" style={{ color: "var(--text-secondary)" }}>
                                      {fmtCurrency(parseFloat(row.vendorQuotedCost) || 0)}
                                    </td>
                                    <td className="proc-td-right" style={{ verticalAlign: "middle" }}>
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        className="proc-num-input"
                                        style={{ width: "85px" }}
                                        value={editValues[`${row.vendorKey}_cost`] !== undefined ? editValues[`${row.vendorKey}_cost`] : (row.vendorCost !== undefined ? row.vendorCost : "")}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          setEditValues(prev => ({ ...prev, [`${row.vendorKey}_cost`]: val }));
                                        }}
                                        onBlur={() => handleCostBlur(po, row)}
                                        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                                      />
                                    </td>
                                    <td className="proc-td-right proc-td-primary">{fmtCurrency(lineTotal)}</td>
                                    <td className="proc-td-center" style={{ whiteSpace: "nowrap" }}>
                                      <div style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                                        <input
                                          type="date"
                                          value={row.vendorEstArrivalStart || ""}
                                          onChange={(e) => handleEstArrivalRangeChange(po, row, "start", e.target.value)}
                                          className="proc-date-input"
                                          style={{ width: "105px", padding: "0.2rem 0.35rem", fontSize: "0.75rem" }}
                                          title="Estimated Arrival Start Date"
                                        />
                                        <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>–</span>
                                        <input
                                          type="date"
                                          value={row.vendorEstArrivalEnd || ""}
                                          onChange={(e) => handleEstArrivalRangeChange(po, row, "end", e.target.value)}
                                          className="proc-date-input"
                                          style={{ width: "105px", padding: "0.2rem 0.35rem", fontSize: "0.75rem" }}
                                          title="Estimated Arrival End Date"
                                        />
                                      </div>
                                    </td>
                                    <td className="proc-td-center">
                                      <button onClick={() => openNotesModal(po.id, row)} title="View / Add Notes" className={`proc-notes-btn ${logCount > 0 ? "has-notes" : ""}`}>
                                        <MessageSquare size={14} />
                                        {logCount > 0 && <span>{logCount}</span>}
                                      </button>
                                    </td>
                                  </tr>
                                );
                              });
                            })()}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Extracted Modals */}
      <ProcurementNotesModal
        isOpen={notesModal.open}
        onClose={() => setNotesModal({ open: false, poId: null, row: null })}
        item={notesModal.row}
        tempNotes={tempNotes}
        setTempNotes={setTempNotes}
        onSubmit={handleSaveLog}
      />

      <ProcurementVendorModal
        isOpen={vendorModal.open}
        onClose={() => setVendorModal({ open: false, poId: null, itemIdx: null, vendorName: "", changing: false })}
        vendorName={vendorModal.vendorName}
        vendorObj={getVendorByName(vendorModal.vendorName)}
        changing={vendorModal.changing}
        setChanging={(val) => setVendorModal(prev => ({ ...prev, changing: val }))}
        vendorSearch={vendorSearch}
        setVendorSearch={setVendorSearch}
        filteredVendors={filteredVendors}
        onChangeVendor={handleChangeVendor}
      />

      <OneOffPurchaseModal
        isOpen={oneOffModalOpen}
        onClose={() => setOneOffModalOpen(false)}
        vendors={vendors}
        loading={submittingOneOff}
        onSubmit={handleOneOffSubmit}
      />

      <SplitLineModal
        isOpen={splitModal.open}
        onClose={() => setSplitModal({ open: false, po: null, itemIdx: null, originalQty: 0 })}
        originalQty={splitModal.originalQty}
        onConfirm={handleConfirmSplit}
      />

      <AllPurchasedModal
        isOpen={allPurchasedModal.open}
        onClose={() => setAllPurchasedModal({ open: false, poNumber: "" })}
        poNumber={allPurchasedModal.poNumber}
      />

      {selectedChangeRequestPo && (
        <ChangeRequestModal
          po={selectedChangeRequestPo}
          onClose={() => setSelectedChangeRequestPo(null)}
        />
      )}

      {selectedTasksPo && (
        <POTasksModal
          isOpen={!!selectedTasksPo}
          po={selectedTasksPo}
          onClose={() => setSelectedTasksPo(null)}
        />
      )}
    </div>
  );
}
